/**
 * GameplayModeRegistry
 *
 * Singleton registry for gameplay mode instances.
 * Provides lookup by mode type ID.
 */

import type { IGameplayMode } from './IGameplayMode';
import type { GameplayModeType } from '../../types/gameplay';
import { PlatformerMode } from './PlatformerMode';
import { SideScrollerMode } from './SideScrollerMode';
import { CinematicMode } from './CinematicMode';

class GameplayModeRegistryImpl {
  private modes: Map<GameplayModeType, IGameplayMode> = new Map();
  private initialized = false;

  /**
   * Initialize with built-in modes
   */
  initialize(): void {
    if (this.initialized) return;

    this.register(new PlatformerMode());
    this.register(new SideScrollerMode());
    this.register(new CinematicMode());

    this.initialized = true;
  }

  /**
   * Register a gameplay mode
   */
  register(mode: IGameplayMode): void {
    this.modes.set(mode.id, mode);
  }

  /**
   * Get a mode by type ID
   * @throws Error if mode not found
   */
  get(type: GameplayModeType): IGameplayMode {
    const mode = this.modes.get(type);
    if (!mode) {
      throw new Error(`Gameplay mode not found: ${type}`);
    }
    return mode;
  }

  /**
   * Try to get a mode, returns undefined if not found
   */
  tryGet(type: GameplayModeType): IGameplayMode | undefined {
    return this.modes.get(type);
  }

  /**
   * Get the cinematic mode instance (typed for scripted commands)
   */
  getCinematicMode(): CinematicMode {
    return this.get('cinematic') as CinematicMode;
  }

  /**
   * Check if registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get all registered mode IDs
   */
  getRegisteredModes(): GameplayModeType[] {
    return Array.from(this.modes.keys());
  }
}

// Singleton instance
export const GameplayModeRegistry = new GameplayModeRegistryImpl();
